import { useState } from "react";
import { CheckCircle2, Video, ArrowRight, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";

interface InterviewTipsCardProps {
  onDismiss: () => void;
}

interface TipSection {
  emoji: string;
  heading: string;
  type: "list" | "prose";
  items?: string[];
  prose?: string;
}

const SECTIONS: TipSection[] = [
  {
    emoji: "📍",
    heading: "Get your environment right",
    type: "list",
    items: [
      "Find a quiet space with no background noise or interruptions",
      "Good lighting — face a window or lamp, not a bright background behind you",
      "Position your camera at eye level — not looking up or down at the screen",
      "Use headphones if possible to reduce echo and improve audio clarity",
      "Check your background is tidy and professional",
    ],
  },
  {
    emoji: "🎯",
    heading: "How to answer each question",
    type: "list",
    items: [
      "Use the STAR method — Situation, Task, Action, Result",
      "Give specific examples from your real experience — not hypothetical answers",
      "Quantify your results where possible — numbers, percentages, and timeframes are more compelling than general statements",
      "Keep answers focused — 60 to 90 seconds is the ideal response length",
      "Speak directly to the camera as if talking to the interviewer — not at your notes",
    ],
  },
  {
    emoji: "🤖",
    heading: "What is being assessed",
    type: "prose",
    prose: `Your responses are scored across four areas: role fit, competency demonstration, communication clarity, and scenario reasoning. The AI assesses what you say and how clearly you say it — not your appearance, accent, or background.\n\nEvery assessment is also reviewed by a human recruiter before any decision is made. The AI score is a guide, not a final verdict.`,
  },
  {
    emoji: "⚡",
    heading: "Last-minute checklist",
    type: "list",
    items: [
      "You have 30 seconds to think before each recording starts — use it",
      "You can retake a question once if you are not happy with your answer",
      "Speak at a normal pace — do not rush",
      "It is okay to pause and think — natural pauses read better than filler words",
      "Be yourself — authentic answers score higher than rehearsed scripts",
    ],
  },
];

export function InterviewTipsCard({ onDismiss }: InterviewTipsCardProps) {
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem("vexis_tips_shown", "true");
    onDismiss();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-start py-6 px-4" data-testid="interview-tips-card">
      <div className="w-full max-w-2xl">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4"
            style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.35)" }}>
            <Lightbulb className="w-6 h-6 text-blue-400" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Before You Begin</h1>
          <p className="text-slate-400 text-sm md:text-base">Quick tips to give your best interview performance</p>
        </div>

        {/* Card body */}
        <div className="rounded-2xl overflow-hidden"
          style={{ background: "#0f172a", border: "1px solid rgba(100,116,139,0.3)" }}>

          {SECTIONS.map((section, idx) => (
            <div key={section.heading}>
              {idx > 0 && <div className="border-t border-slate-800 mx-5" />}
              <div className="px-5 py-5 md:px-8 md:py-6">
                <h2 className="text-base md:text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span>{section.emoji}</span>
                  {section.heading}
                </h2>

                {section.type === "list" && section.items && (
                  <ul className="space-y-2">
                    {section.items.map((tip) => (
                      <li key={tip} className="flex items-start gap-3">
                        <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300 text-sm md:text-base leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.type === "prose" && section.prose && (
                  <div className="space-y-3">
                    {section.prose.split("\n\n").map((para, i) => (
                      <p key={i} className="text-slate-300 text-sm md:text-base leading-relaxed">{para}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Dismiss area */}
          <div className="px-5 py-5 md:px-8 md:py-6 border-t border-slate-800"
            style={{ background: "rgba(30,41,59,0.5)" }}>
            <Button
              onClick={handleDismiss}
              className="w-full md:w-auto md:mx-auto md:flex items-center justify-center gap-2 font-semibold h-12 text-base px-8"
              style={{ backgroundColor: "#2563eb", color: "#ffffff" }}
              data-testid="button-tips-ready"
            >
              I&apos;m Ready — Start My Interview
              <ArrowRight className="w-4 h-4" />
            </Button>

            <label className="flex items-center gap-2 mt-4 cursor-pointer w-fit mx-auto md:mx-0">
              <input
                type="checkbox"
                checked={neverShowAgain}
                onChange={(e) => {
                  setNeverShowAgain(e.target.checked);
                  if (e.target.checked) {
                    localStorage.setItem("vexis_tips_shown", "true");
                  } else {
                    localStorage.removeItem("vexis_tips_shown");
                  }
                }}
                className="w-4 h-4 rounded accent-blue-500 cursor-pointer"
                data-testid="checkbox-tips-never-show"
              />
              <span className="text-xs text-slate-500 select-none">Don&apos;t show this again</span>
            </label>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-600 mt-4">
          Your camera and microphone are ready. Recording begins after you dismiss this screen.
        </p>
      </div>
    </div>
  );
}
